/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  registrationDateTimeToUtcIso,
  registrationDateTimeFromUtcIso,
} from '@sportninja/common/ts-utils/registration-datetime'
import { formatTimezoneLabel } from '@sportninja/common/ts-utils/timezone-display'
import Text from 'src/components/ts-components/Text'
import RegistrationWindowFields, {
  RegistrationWindowChange,
} from './RegistrationWindowFields'
import { useRegistrationSetupContext } from '../../context'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
// import { SelectInput } from 'src/components/ts-components/SelectInput'
import MultipleOptionsInput from 'src/components/ts-components/MultipleOptionsInput'
import colors from '@sportninja/common/constants/appColors'
import CopyComponent from 'src/components/CopyComponent'
import { indentedGroupStyle } from '../indentedGroupStyle'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const GeneralSettings = () => {
  const { state, setState, registrationId, apiAccount } =
    useRegistrationSetupContext()

  const accountTimezone = apiAccount?.address?.timezone

  // Shows 'PDT' / 'EST' rather than the raw IANA name, and marks the case where
  // the account has no timezone and the viewer's own is being used instead.
  const timezoneLabel = `Registration Start/End (${formatTimezoneLabel(
    accountTimezone
  )})`

  // The window is stored as UTC ISO strings but edited in the account's
  // timezone, so convert on the way in and back out again.
  const startsAt = registrationDateTimeFromUtcIso(
    state?.competitionDateRange?.starts_at,
    accountTimezone
  )
  const endsAt = registrationDateTimeFromUtcIso(
    state?.competitionDateRange?.ends_at,
    accountTimezone
  )

  const setCompetitionWindow = ({
    startsAt: nextStart,
    endsAt: nextEnd,
  }: RegistrationWindowChange) => {
    const nextStartsAt = registrationDateTimeToUtcIso(
      nextStart,
      accountTimezone
    )
    const nextEndsAt = registrationDateTimeToUtcIso(nextEnd, accountTimezone)
    const bothFilled = Boolean(nextStartsAt && nextEndsAt)
    const nextCustomError = { ...(state.customError || {}) }
    if (bothFilled) {
      delete nextCustomError.competitionDateRange
    }
    setState({
      ...state,
      competitionDateRange: {
        starts_at: nextStartsAt,
        ends_at: nextEndsAt,
      },
      fieldErrors: bothFilled
        ? state.fieldErrors.filter((f) => f !== 'competitionDateRange')
        : state.fieldErrors,
      customError: nextCustomError,
    })
  }

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
          justify-content: space-between;
          width: 100%;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          General Settings
        </Text>
        {registrationId ? (
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <CopyComponent
              id={registrationId}
              customText='Registration ID'
              alternativeBackground={true}
            />
          </div>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          width: 50%;
          min-width: 380px;
        `}
      >
        <RegistrationWindowFields
          label={timezoneLabel}
          startsAt={startsAt}
          endsAt={endsAt}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          onChange={setCompetitionWindow}
        />
      </div>
      <div
        css={css`
          width: 100%;
          height: 1px;
          background: ${colors.NEUTRAL_100};
          border-bottom: 1px solid ${colors.NEUTRAL_100};
        `}
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionSmall' weight='semiBold'>
          Registration Type
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Enable Team Registration'
          subtitle='Allow Team Officials To Register To Their Team'
          value={state.enableTeamRegistration}
          onChange={(value) => {
            setState({ ...state, enableTeamRegistration: value })
          }}
          dataTestId='enable-team-registration-switch'
        />
      </div>
      {/*
        SN-4709 (Jeff, Sep 1) — auto-approve used to be ONE switch under Other
        Settings: "that's agnostic, we don't differentiate between team or
        player, and that's the problem, because one is not good for the other."
        Each half now sits under the registration type it governs, behind the
        house nesting rail, so the relationship is visible rather than inferred.
      */}
      <div css={indentedGroupStyle}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 24px;
            align-self: stretch;
          `}
        >
          <SwitchInput
            title='Auto-Approve Team Registrations'
            subtitle='Registering Teams Will Be Approved Automatically'
            value={state.autoApproveTeamRegistrations}
            onChange={(value) => {
              setState({ ...state, autoApproveTeamRegistrations: value })
            }}
            disabled={!state.enableTeamRegistration}
            dataTestId='auto-approve-team-switch'
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Enable Player Registration'
          subtitle='Allow Players to Register to Teams and/or Drafts'
          value={state.enablePlayerRegistration}
          onChange={(value) => {
            setState({
              ...state,
              enablePlayerRegistration: value,
            })
          }}
          dataTestId='enable-player-registration-switch'
        />
      </div>
      <div css={indentedGroupStyle}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 24px;
            align-self: stretch;
          `}
        >
          <SwitchInput
            title='Auto-Approve Player Registrations'
            subtitle='Registering Players Will Be Approved Automatically'
            value={state.autoApprovePlayerRegistrations}
            onChange={(value) => {
              setState({ ...state, autoApprovePlayerRegistrations: value })
            }}
            disabled={!state.enablePlayerRegistration}
            dataTestId='auto-approve-player-switch'
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Add Players Outside Of Registration'
          subtitle='Allow Team Officials To Be Able To Add Players Outside Of Registration'
          value={state.addPlayersOutsideOfRegistration}
          onChange={(value) => {
            setState({ ...state, addPlayersOutsideOfRegistration: value })
          }}
        />
      </div>
      {/* Removing this for now in favor of the switch input */}
      {/* <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <SelectInput
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            label='Registration Type'
            options={[
              { label: 'Team', value: 'team' },
              { label: 'Player', value: 'player' },
            ]}
            value={state.registrationType}
            onChange={(value) => {
              setState({
                ...state,
                registrationType: value as 'player' | 'team',
              })
            }}
            disabled={false}
            name='registrationType'
          />
        </div>
      </div> */}
      {/* separator */}
      <div
        css={css`
          width: 100%;
          height: 1px;
          background: ${colors.NEUTRAL_100};
          border-bottom: 1px solid ${colors.NEUTRAL_100};
        `}
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionSmall' weight='semiBold'>
          Other Settings
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        {/* The state is an enum ('teams' | 'divisions') while the control is a
            boolean, so the two are mapped here rather than storing a boolean:
            a 'default to divisions' flag would encode today's default in its
            false case, and a third tab would then need a data migration. */}
        <SwitchInput
          title='Default To Divisions Tab'
          subtitle='Open Registration On The Divisions Tab Instead Of Teams'
          value={state.defaultRegistrationTab === 'divisions'}
          onChange={(value) => {
            setState({
              ...state,
              defaultRegistrationTab: value ? 'divisions' : 'teams',
            })
          }}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <MultipleOptionsInput
          label='BCC Registration Emails:'
          tip='(Comma Separated, Press Enter To Add)'
          value={state.bccEmails}
          onChange={(value) => {
            setState({ ...state, bccEmails: value })
          }}
          placeholder='Enter email addresses'
          type='email'
          validator={emailRegex}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          name='bccEmails'
        />
      </div>
    </div>
  )
}

export default GeneralSettings
